import { formatDistanceToNow } from 'date-fns';
import {
  Calendar,
  Edit2,
  Globe,
  MoreVertical,
  Star,
  Trash2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { SmsTemplateSchema } from '@/services/api/generated/smsTemplatesService';

interface SmsTemplateCardProps {
  template: SmsTemplateSchema;
  onEdit?: (template: SmsTemplateSchema) => void;
  onDelete?: (template: SmsTemplateSchema) => void;
  canEdit?: boolean;
}

const CATEGORY_COLORS: Record<string, string> = {
  'Document Collection': 'bg-purple-100 text-purple-700',
  'Initial Treatment Assessment': 'bg-blue-100 text-blue-700',
  'Ongoing Treatment Check-In': 'bg-green-100 text-green-700',
};

export const SmsTemplateCard = ({
  template,
  onEdit,
  onDelete,
  canEdit = false,
}: SmsTemplateCardProps) => {
  const categoryColor =
    CATEGORY_COLORS[template.category] ?? 'bg-zinc-100 text-zinc-700';

  return (
    <div className="rounded-lg border bg-white p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-zinc-900 truncate">
              {template.name}
            </h3>
            {template.is_default && (
              <span className="flex items-center gap-0.5 text-amber-600">
                <Star className="h-3 w-3 fill-current" />
              </span>
            )}
            {template.is_global && (
              <Globe className="h-3.5 w-3.5 text-zinc-400" />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span
              className={cn('text-xs px-2 py-0.5 rounded-full', categoryColor)}
            >
              {template.category}
            </span>
            {template.cadence_day != null && (
              <span className="flex items-center gap-1 text-xs text-zinc-500">
                <Calendar className="h-3 w-3" />
                Day {template.cadence_day}+
              </span>
            )}
            {!template.is_active && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">
                Inactive
              </span>
            )}
          </div>

          <p className="text-sm text-zinc-600 line-clamp-2 mb-2">
            {template.content}
          </p>

          <p className="text-xs text-zinc-400">
            Modified{' '}
            {formatDistanceToNow(new Date(template.modified_at), {
              addSuffix: true,
            })}
          </p>
        </div>

        {canEdit && (onEdit || onDelete) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(template)}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  onClick={() => onDelete(template)}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
};
